/**
 * Frontend Stripe Service
 * Handles Stripe integration for payment processing
 * Enhanced with Firebase Extension integration
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import firebaseStripeService, { CheckoutSession } from './firebaseStripeService';

// Define Product type locally
interface Product {
  id: string;
  name: string;
  description?: string;
}

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  features: string[];
  popular?: boolean;
  product?: Product;
  monthlyPriceId?: string;
  annualPriceId?: string;
}

export interface CheckoutSessionRequest {
  planId: string;
  billingCycle: 'monthly' | 'annual';
  successUrl: string;
  cancelUrl: string;
}

export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Frontend Stripe Service Class
 */
class StripeService {
  private stripe: Promise<Stripe | null>;

  constructor() {
    this.stripe = stripePromise;
  }

  /**
   * Get Stripe instance
   */
  async getStripe(): Promise<Stripe | null> {
    return await this.stripe;
  }

  /**
   * Redirect to Stripe Checkout
   */
  async redirectToCheckout(sessionId: string): Promise<{ error?: any }> {
    const stripe = await this.getStripe();
    if (!stripe) {
      throw new Error('Stripe not loaded');
    }

    const { error } = await stripe.redirectToCheckout({
      sessionId,
    });

    return { error };
  }

  /**
   * Create checkout session using Firebase Extension
   */
  async createCheckoutSession(
    planId: string,
    billingCycle: 'monthly' | 'annual',
    successUrl: string,
    cancelUrl: string
  ): Promise<void> {
    try {
      // Get the price ID based on plan and billing cycle
      const priceId = await this.getPriceIdForPlan(planId, billingCycle);

      if (!priceId) {
        throw new Error(`Price not found for plan ${planId} with ${billingCycle} billing`);
      }

      // Log checkout session data for debugging
      console.log('Creating checkout session:', {
        planId,
        billingCycle,
        priceId
      });

      // Create checkout session using Firebase extension
      const sessionData: CheckoutSession = {
        success_url: successUrl,
        cancel_url: cancelUrl,
        price: priceId,
        quantity: 1,
        mode: 'subscription',
        allow_promotion_codes: true,
        metadata: {
          planId,
          billingCycle
        }
      };

      const { url } = await firebaseStripeService.createCheckoutSession(sessionData);

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Create one-time payment checkout session
   */
  async createPaymentSession(
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const sessionData: CheckoutSession = {
        success_url: successUrl,
        cancel_url: cancelUrl,
        price: priceId,
        quantity: 1,
        mode: 'payment',
        allow_promotion_codes: true,
        metadata
      };

      const { url } = await firebaseStripeService.createCheckoutSession(sessionData);
      window.location.href = url;
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw error;
    }
  }

  /**
   * Open billing portal using Firebase Extension
   */
  async openBillingPortal(returnUrl: string): Promise<void> {
    try {
      const { url } = await firebaseStripeService.createPortalSession(returnUrl);
      window.location.href = url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      throw error;
    }
  }

  /**
   * Get current subscription using Firebase Extension
   */
  async getCurrentSubscription(): Promise<UserSubscription | null> {
    try {
      const subscriptions = await firebaseStripeService.getCurrentSubscriptions();
      const activeSub = subscriptions.find(sub =>
        sub.status === 'active' || sub.status === 'trialing'
      );

      if (!activeSub) return null;

      // Convert Firebase subscription to UserSubscription format
      return {
        id: activeSub.id,
        userId: '', // Will be filled by auth context
        planId: activeSub.price.metadata?.planId || activeSub.price.product,
        status: activeSub.status as any,
        currentPeriodStart: activeSub.current_period_start.toDate(),
        currentPeriodEnd: activeSub.current_period_end.toDate(),
        cancelAtPeriodEnd: activeSub.cancel_at_period_end,
        trialEnd: activeSub.trial_end?.toDate(),
        createdAt: activeSub.created.toDate(),
        updatedAt: activeSub.created.toDate()
      };
    } catch (error) {
      console.error('Error getting current subscription:', error);
      return null;
    }
  }

  /**
   * Get subscription plans from Firebase Extension with fallback
   */
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const products = await firebaseStripeService.getProducts();
      const plans: SubscriptionPlan[] = [];

      for (const product of products) {
        const prices = await firebaseStripeService.getPricesForProduct(product.id);

        // Group prices by billing cycle
        const monthlyPrice = prices.find(p => p.interval === 'month');
        const annualPrice = prices.find(p => p.interval === 'year');

        if (monthlyPrice || annualPrice) {
          plans.push({
            id: product.id,
            name: product.name,
            description: product.description || '',
            price: {
              monthly: monthlyPrice ? monthlyPrice.unit_amount / 100 : 0,
              annual: annualPrice ? annualPrice.unit_amount / 100 : 0
            },
            features: product.metadata?.features ?
              JSON.parse(product.metadata.features) : [],
            popular: product.metadata?.popular === 'true',
            product,
            monthlyPriceId: monthlyPrice?.id,
            annualPriceId: annualPrice?.id
          });
        }
      }

      if (plans.length > 0) {
        return plans;
      }
    } catch (error) {
      console.warn('Firestore products not available, using fallback plans:', error);
    }

    // Fallback plans when Firestore is not available
    return this.getFallbackPlans();
  }

  /**
   * Get fallback subscription plans
   */
  private getFallbackPlans(): SubscriptionPlan[] {
    return [
      {
        id: 'heat-pro',
        name: 'HEAT Pro',
        description: 'Advanced training features with AI insights and wearable integration',
        price: {
          monthly: 9.99,
          annual: 99.99
        },
        features: [
          'AI-powered training insights',
          'Wearable device integration',
          'Advanced analytics',
          'Ad-free experience',
          'Priority support'
        ],
        popular: false,
        monthlyPriceId: 'price_1RhVvNGRz61xtKoIFWBWG7y9', // HEAT Pro Monthly
        annualPriceId: 'price_1RhVvNGRz61xtKoIN1csQE85'   // HEAT Pro Annual
      },
      {
        id: 'heat-elite',
        name: 'HEAT Elite',
        description: 'Premium training experience for serious athletes',
        price: {
          monthly: 19.99,
          annual: 199.99
        },
        features: [
          'Everything in Pro',
          'Elite athlete templates',
          'Personal coaching insights',
          'Competition preparation',
          'Priority customer support',
          'Early access to new features'
        ],
        popular: true,
        monthlyPriceId: 'price_1RhVvNGRz61xtKoIAxRkB4eG', // HEAT Elite Monthly
        annualPriceId: 'price_1RhVvNGRz61xtKoIdD2Aq0nh'   // HEAT Elite Annual
      }
    ];
  }

  /**
   * Get price ID for a plan and billing cycle
   */
  private async getPriceIdForPlan(planId: string, billingCycle: 'monthly' | 'annual'): Promise<string | null> {
    try {
      const plans = await this.getSubscriptionPlans();
      const plan = plans.find(p => p.id === planId);

      if (!plan) return null;

      return billingCycle === 'monthly' ? plan.monthlyPriceId || null : plan.annualPriceId || null;
    } catch (error) {
      console.error('Error getting price ID:', error);
      return null;
    }
  }

  /**
   * Check subscription status using Firebase Extension
   */
  async hasActiveSubscription(): Promise<boolean> {
    return await firebaseStripeService.hasActiveSubscription();
  }

  /**
   * Get subscription tier using Firebase Extension
   */
  async getSubscriptionTier(): Promise<'free' | 'pro' | 'elite'> {
    return await firebaseStripeService.getSubscriptionTier();
  }

  /**
   * Subscribe to subscription changes
   */
  subscribeToSubscriptions(callback: (subscription: UserSubscription | null) => void): () => void {
    return firebaseStripeService.subscribeToSubscriptions((subscriptions) => {
      const activeSub = subscriptions.find(sub =>
        sub.status === 'active' || sub.status === 'trialing'
      );

      if (activeSub) {
        const userSub: UserSubscription = {
          id: activeSub.id,
          userId: '',
          planId: activeSub.price.metadata?.planId || activeSub.price.product,
          status: activeSub.status as any,
          currentPeriodStart: activeSub.current_period_start.toDate(),
          currentPeriodEnd: activeSub.current_period_end.toDate(),
          cancelAtPeriodEnd: activeSub.cancel_at_period_end,
          trialEnd: activeSub.trial_end?.toDate(),
          createdAt: activeSub.created.toDate(),
          updatedAt: activeSub.created.toDate()
        };
        callback(userSub);
      } else {
        callback(null);
      }
    });
  }

  /**
   * Check if user has access to premium features using Firebase Extension
   */
  async hasFeatureAccess(feature: 'ai' | 'wearables' | 'analytics' | 'adFree' | 'priority'): Promise<boolean> {
    return await firebaseStripeService.hasFeatureAccess(feature);
  }

  /**
   * Legacy method for backward compatibility
   */
  hasFeatureAccessSync(
    subscription: UserSubscription | null,
    feature: 'ai' | 'wearables' | 'analytics' | 'adFree' | 'priority'
  ): boolean {
    if (!subscription || subscription.status !== 'active') {
      return false;
    }

    const plan = subscription.planId;

    switch (feature) {
      case 'adFree':
        return ['pro', 'elite'].includes(plan);
      case 'ai':
        return ['pro', 'elite'].includes(plan);
      case 'wearables':
        return ['pro', 'elite'].includes(plan);
      case 'analytics':
        return ['pro', 'elite'].includes(plan);
      case 'priority':
        return plan === 'elite';
      default:
        return false;
    }
  }

  /**
   * Get plan display name
   */
  getPlanDisplayName(planId: string): string {
    const planNames: Record<string, string> = {
      free: 'Free',
      pro: 'Pro',
      elite: 'Elite',
    };
    return planNames[planId] || 'Unknown';
  }

  /**
   * Get plan color for UI
   */
  getPlanColor(planId: string): string {
    const planColors: Record<string, string> = {
      free: 'gray',
      pro: 'primary',
      elite: 'purple',
    };
    return planColors[planId] || 'gray';
  }

  /**
   * Format subscription status for display
   */
  formatSubscriptionStatus(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'Active',
      canceled: 'Cancelled',
      past_due: 'Past Due',
      unpaid: 'Unpaid',
      trialing: 'Trial',
    };
    return statusMap[status] || status;
  }

  /**
   * Calculate savings for annual billing
   */
  calculateAnnualSavings(monthlyPrice: number, annualPrice: number): number {
    const monthlyTotal = monthlyPrice * 12;
    return monthlyTotal - annualPrice;
  }

  /**
   * Get savings percentage for annual billing
   */
  getAnnualSavingsPercentage(monthlyPrice: number, annualPrice: number): number {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - annualPrice;
    return Math.round((savings / monthlyTotal) * 100);
  }
}

export const stripeService = new StripeService();
export default stripeService;
